import { useEffect, useRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { createPortal } from "react-dom";

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

export function Card({
  children,
  className,
  glow: _glow = false,
}: {
  children: ReactNode;
  className?: string;
  /**
   * @deprecated No-op. Previously toggled the operator-TUI `card-glow-teal`
   * box-shadow. The new theme uses a flat 1px border + bg lift instead.
   * Kept on the type so callers in unowned files (Dashboard, etc.) still
   * type-check. @ux can drop the prop once those callers migrate.
   */
  glow?: boolean;
}) {
  return (
    <div
      className={cx(
        "rounded-[var(--radius-card)] bg-surface border border-border",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function Label({ children }: { children: ReactNode }) {
  return (
    <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-mute">
      {children}
    </label>
  );
}

export function PromptLabel({ children }: { children: ReactNode }) {
  return (
    <label className="mb-1.5 flex items-center gap-2 font-mono text-[12px] text-ink-2">
      <span className="text-mute">{children}</span>
      <span className="text-accent">›</span>
    </label>
  );
}

const fieldSkin =
  "rounded-[var(--radius-control)] bg-inset px-3 py-2.5 text-sm text-ink outline-none hairline placeholder:text-ink-2/60 focus:border-accent";
const fieldClass = `w-full ${fieldSkin}`;

export function Field({
  label,
  hint,
  children,
  prompt = false,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  prompt?: boolean;
}) {
  return (
    <div>
      {prompt ? <PromptLabel>{label}</PromptLabel> : <Label>{label}</Label>}
      {children}
      {hint ? <p className="mt-1.5 text-xs text-mute">{hint}</p> : null}
    </div>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cx(fieldClass, props.className)} />;
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={cx(fieldClass, "min-h-28 resize-y font-mono text-[13px]", props.className)}
    />
  );
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  const { className, children, style, ...rest } = props;
  const hasWidth = Boolean(className && /(^|\s)w-/.test(className));
  return (
    <select
      {...rest}
      style={{ colorScheme: "dark", ...style }}
      className={cx(
        fieldSkin,
        !hasWidth && "w-full",
        "max-w-full min-w-0 text-ink [&>option]:bg-raised [&>option]:text-ink",
        className,
      )}
    >
      {children}
    </select>
  );
}

export function MenuSelect({
  value,
  onChange,
  options,
  className,
  "aria-label": ariaLabel,
  "data-field": dataField,
}: {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  className?: string;
  "aria-label"?: string;
  "data-field"?: string;
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const selected =
    options.find((option) => option.value === value) ??
    options.find((option) => option.value.toLowerCase() === String(value ?? "").toLowerCase());
  const currentLabel = (selected?.label || String(value ?? "")).trim();

  function place() {
    const el = buttonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const width = Math.max(rect.width, 176);
    let left = rect.left;
    if (left + width > window.innerWidth - 8) left = Math.max(8, window.innerWidth - width - 8);
    const menuH = Math.min(options.length * 36 + 8, 280);
    let top = rect.bottom + 4;
    if (top + menuH > window.innerHeight - 8 && rect.top > menuH) {
      top = rect.top - menuH - 4;
    }
    setPos({ top, left, width });
  }

  useEffect(() => {
    if (!open) return;
    place();
    const onDoc = (event: MouseEvent) => {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onWin = () => place();
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
    };
  }, [open, options.length]);

  const menu =
    open && pos
      ? createPortal(
          <ul
            ref={menuRef}
            role="listbox"
            aria-label={ariaLabel}
            className="fixed z-[80] max-h-72 overflow-auto rounded-[var(--radius-control)] border border-border bg-raised py-1 text-sm text-ink shadow-lg [&>li>button]:bg-raised [&>li>button]:text-ink"
            style={{ top: pos.top, left: pos.left, width: pos.width, colorScheme: "dark" }}
          >
            {options.map((option) => (
              <li key={option.value} role="option" aria-selected={option.value === value}>
                <button
                  type="button"
                  className={cx(
                    "flex w-full px-3 py-2 text-left text-ink hover:bg-accent-soft",
                    option.value === value && "bg-accent-soft text-accent",
                  )}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </ul>,
          document.body,
        )
      : null;

  const fullWidth = Boolean(className && /(^|\s)w-full(\s|$)/.test(className));
  return (
    <div className={cx("relative z-30 shrink-0 overflow-visible", className)}>
      <button
        ref={buttonRef}
        type="button"
        data-field={dataField}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={currentLabel}
        className={cx(
          fieldSkin,
          "inline-flex items-center justify-between gap-2 overflow-hidden text-ink",
          fullWidth ? "w-full min-w-0" : "min-w-[12rem] max-w-[18rem]",
        )}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="truncate">{currentLabel}</span>
        <span className="text-mute">▾</span>
      </button>
      {menu}
    </div>
  );
}

type BtnVariant = "primary" | "ghost" | "subtle" | "danger" | "success" | "amber";

const btn: Record<BtnVariant, string> = {
  primary:
    "bg-accent text-canvas hover:bg-accent-hover disabled:bg-white/5 disabled:text-mute",
  ghost:
    "bg-transparent text-ink hover:bg-white/5 disabled:text-mute hairline",
  subtle:
    "bg-surface text-ink hover:bg-raised border border-border disabled:text-mute",
  danger:
    "bg-fail/15 text-fail hover:bg-fail/25 disabled:text-mute disabled:bg-transparent",
  success:
    "bg-ok/15 text-ok hover:bg-ok/25 disabled:text-mute disabled:bg-transparent",
  amber:
    "bg-live text-canvas hover:bg-warn disabled:bg-white/5 disabled:text-mute",
};

export function Button({
  variant = "primary",
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
/**
    * `variant` accepts the six current values, OR the legacy `"teal"` string.
    * `"teal"` is treated as `"primary"` at runtime so unowned callers
    * (Leads, History, Setup, Scripts, Dashboard, Sip, Settings, Login)
    * keep compiling. @ux migrates those callers; the `"teal"` branch is
    * dropped with that migration.
    */
  variant?: BtnVariant | "teal";
}) {
  const effective: BtnVariant = variant === "teal" ? "primary" : variant;
  return (
    <button
      type={props.type ?? "button"}
      {...props}
      className={cx(
        "inline-flex items-center justify-center gap-2 rounded-[var(--radius-control)] px-3.5 py-2 text-sm font-medium tracking-wide transition disabled:cursor-not-allowed",
        btn[effective],
        className,
      )}
    />
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-start gap-3 rounded-[var(--radius-card)] bg-surface border border-border px-6 py-10">
      <div className="font-mono text-[11px] tracking-[0.18em] text-accent">cdialer ›</div>
      <h2 className="text-base font-medium text-ink">{title}</h2>
      <p className="max-w-lg text-sm leading-relaxed text-mute">{body}</p>
      {action}
    </div>
  );
}

export function PageHeader({
  kicker,
  title,
  actions,
}: {
  kicker?: string;
  title: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        {kicker ? (
          <div className="mb-1 text-[11px] font-medium uppercase tracking-[0.18em] text-mute">
            {kicker}
          </div>
        ) : null}
        <h1 className="text-2xl font-medium tracking-tight text-ink">{title}</h1>
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function PageBody({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const hasOverflow = Boolean(className && /(^|\s)overflow-/.test(className));
  return (
    <div
      className={cx(
        "scroll-thin min-h-0 flex-1 p-6 lg:p-8",
        !hasOverflow && "overflow-auto",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function Check({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2.5 text-sm text-ink">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 accent-accent"
      />
      {label}
    </label>
  );
}

export function StatusPill({
  tone = "mute",
  children,
}: {
  tone?: "teal" | "ok" | "fail" | "live" | "mute";
  children: ReactNode;
}) {
  return <span className={cx("pill", `pill-${tone}`)}>{children}</span>;
}

/**
 * AMD / match / SIP pill mapping.
 */
export function amdTone(value: string | null | undefined): "teal" | "fail" | "mute" | "live" {
  const raw = (value ?? "").toUpperCase();
  if (raw.includes("SCREEN") || raw.includes("📱")) return "live";
  if (raw.includes("HUMAN") || raw.includes("👤")) return "teal";
  if (raw.includes("MACHINE") || raw.includes("🤖") || /(^|\s)VM(\s|$)/.test(raw)) return "fail";
  return "mute";
}